import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { PencilRuler, Sparkles, LayoutGrid, ArrowRight, Flame, CheckCircle2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { tr } from "@/data/i18n";

const HERO = "https://images.unsplash.com/photo-1526506118085-60ce8714f8c5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export default function Dashboard() {
  const { lang, user, subscription } = useAuth();
  const navigate = useNavigate();

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const modes = [
    { icon: <PencilRuler />, title: tr(lang, "mode_manual_title"), sub: tr(lang, "mode_manual_sub"), desc: tr(lang, "mode_manual_desc"), path: "/create/manual", testid: "mode-card-manual", tag: "Fai da Te" },
    { icon: <Sparkles />, title: tr(lang, "mode_ai_title"), sub: tr(lang, "mode_ai_sub"), desc: tr(lang, "mode_ai_desc"), path: "/create/ai", testid: "mode-card-ai", tag: tr(lang, "mode_ai_sub"), featured: true },
    { icon: <LayoutGrid />, title: tr(lang, "mode_std_title"), sub: tr(lang, "mode_std_sub"), desc: tr(lang, "mode_std_desc"), path: "/templates", testid: "mode-card-standard", tag: "Pronto" },
  ];

  return (
    <div className="relative">
      {/* Hero */}
      <section className="relative overflow-hidden grain">
        <div className="absolute inset-0">
          <img src={HERO} alt="" className="h-full w-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-background/70 via-background/85 to-background" />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 pt-20 pb-16 sm:pt-28 sm:pb-24">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="inline-flex items-center gap-2 rounded-full border border-red-500/30 bg-red-500/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-red-400">
              <Flame size={14} /> {tr(lang, "trial_free")}
            </div>
            <h1 className="mt-5 max-w-3xl font-display text-5xl font-extrabold uppercase leading-[0.95] tracking-tight text-white sm:text-7xl">
              {tr(lang, "hero_title")}
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-zinc-400 sm:text-lg">{tr(lang, "hero_sub")}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <button onClick={() => navigate("/create/ai")} data-testid="hero-cta-btn"
                      className="inline-flex items-center gap-2 rounded-full bg-red-500 px-7 py-3.5 font-semibold text-white transition-all hover:bg-red-600 red-glow">
                {tr(lang, "hero_cta")} <ArrowRight size={18} />
              </button>
              {!user && (
                <button onClick={login} className="rounded-full border border-zinc-700 bg-zinc-900/80 px-7 py-3.5 font-semibold text-zinc-200 transition-all hover:border-red-500/40">
                  {tr(lang, "login")}
                </button>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Trial banner for logged users */}
      {user && subscription && !subscription.subscribed && (
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className={`-mt-4 rounded-2xl border p-4 text-sm ${subscription.trial_active ? "border-orange-500/30 bg-orange-500/10 text-orange-300" : "border-red-500/40 bg-red-500/10 text-red-300"}`}>
            {subscription.trial_active
              ? `⚡ ${subscription.trial_days_left} ${tr(lang, "trial_days_left")}`
              : <span>{tr(lang, "trial_ended")} <button onClick={() => navigate("/pricing")} className="underline font-semibold">{tr(lang, "nav_pricing")}</button></span>}
          </div>
        </div>
      )}

      {/* Mode selection */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 py-16">
        <h2 className="mb-8 font-display text-2xl font-bold uppercase tracking-tight text-white sm:text-4xl">{tr(lang, "choose_mode")}</h2>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {modes.map((m, i) => (
            <motion.button key={m.path} onClick={() => navigate(m.path)} data-testid={m.testid}
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className={`group relative overflow-hidden rounded-3xl border p-8 text-left transition-all hover:-translate-y-1 ${
                m.featured ? "border-red-500/50 bg-gradient-to-br from-red-500/10 to-zinc-950 red-glow" : "border-zinc-800 bg-zinc-900/50 hover:border-red-500/40"}`}>
              <div className={`mb-6 flex h-14 w-14 items-center justify-center rounded-2xl ${m.featured ? "bg-red-500 text-white" : "bg-zinc-800 text-red-400"}`}>
                {m.icon}
              </div>
              <span className="text-xs font-bold uppercase tracking-widest text-red-400">{m.tag}</span>
              <h3 className="mt-1 font-display text-2xl font-bold uppercase text-white">{m.title}</h3>
              <p className="mt-0.5 text-sm font-medium text-zinc-500">{m.sub}</p>
              <p className="mt-4 text-sm leading-relaxed text-zinc-400">{m.desc}</p>
              <div className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-red-400">
                {tr(lang, "start")} <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </div>
            </motion.button>
          ))}
        </div>

        {/* Feature strip */}
        <div className="mt-14 grid gap-4 sm:grid-cols-3">
          {[tr(lang, "beginner_hint"), tr(lang, "hero_sub"), tr(lang, "pricing_sub")].map((f, i) => (
            <div key={i} className="flex items-start gap-3 rounded-2xl border border-zinc-800 bg-zinc-900/40 p-5">
              <CheckCircle2 className="mt-0.5 shrink-0 text-red-500" size={18} />
              <p className="text-sm text-zinc-400">{f}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
